import { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import { Button, CircularProgress, styled, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectUser } from '@/store/reducers/user/userSlice';
import analytics from '@/utils/analytics/analyticsInstance';
import { ReactComponent as LogoDealpumpItalic } from '@/assets/images/dealpump-italic.svg';
import iphoneImage from '@/assets/images/iphone.png';
import config from '@/types/config';
import { sendAnalyticsEvent } from '@/store/reducers/events/actions';
import { EventsEnum } from '@/constants/events';

const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
  height: '100%',
  backgroundColor: theme.palette.secondary.light,
}));

const Title = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: 28,
  textAlign: 'center',
  lineHeight: '32px',
  marginBottom: theme.spacing(1),
}));

const IPhoneImage = styled('img')(({ theme }) => ({
  width: '90%',
  height: 'auto',
}));

const GradientBackground = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: `linear-gradient(190deg, rgba(255, 255, 255, 0) 70%, rgba(204, 245, 38, 1) 100%)`,
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  paddingBlock: 16,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderBottomWidth: 1,
  borderBottomColor: theme.palette.border.main,
  borderBottomStyle: 'solid',
}));

const TopContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(2),
  gap: theme.spacing(2),
}));

const BottomContainer = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
  width: '100%',
  position: 'relative',
}));

export default function DownloadAppPage() {
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    analytics.track('webOnboardingScreenOpened', { screen_title: 'Get Findies App' });
  }, []);

  const handleContinue = async () => {
    try {
      setIsLoading(true);
      await dispatch(sendAnalyticsEvent({ eventName: EventsEnum.initiateCheckout }));
      await analytics.forceTrack('webToAppButtonClicked');

      if (user) {
        window.location.href = config.oneLinkUrl;
      } else {
        navigate(ROUTES.SURVEY);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container>
      <Grid container sx={{ flex: 1, flexDirection: 'column' }}>
        <Grid
          item
          xs={12}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <LogoContainer>
            <LogoDealpumpItalic width={90} height={20} />
          </LogoContainer>
          <TopContainer>
            <Title variant="h4">Install the app to unlock your feed and rewards</Title>
            <Button
              size="large"
              variant="contained"
              disabled={isLoading}
              startIcon={isLoading && <CircularProgress size={16} color="inherit" />}
              onClick={handleContinue}
            >
              Get Findies App
            </Button>
          </TopContainer>
          <BottomContainer>
            <IPhoneImage src={iphoneImage} />
            <GradientBackground />
          </BottomContainer>
        </Grid>
      </Grid>
    </Container>
  );
}
